import { cn } from 'component-library/utils';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  THREAT_TYPES,
  ThreatType,
} from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { NoChartData } from '@/components/Charts';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataFromGraphFact, sortChartData } from '@/components/Charts/utils';
import { GenericTable } from '@/components/widgets/o/GenericTable/GenericTable';
import { SortButton } from '@/components/widgets/o/GenericTable/SortButton';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';
import { printYear } from '@/utils/formatting/date';
import { formatGraphFactToArea } from '@/utils/formatting/formatGraphFactToArea';
import { MetadataColumn } from '@/utils/table/mergeColumns';

const EMPTY_VALUE = '-';
const TABLE_KEYS = { THREATS: 'threats', AREA_PCT: 'area_pct', AREA_5Y_PCT: 'area_5y_pct' };

const factNames = {
  threat_areas: 'threat_areas',
};

type TableColumn = MetadataColumn<(typeof TABLE_KEYS)[keyof typeof TABLE_KEYS] | string>;

export type HabitatIntactnessThreatsHistoricTableProps = {
  threatsTimeSeries: GraphFact[];
  threatsAreaPct?: PlotReportFactElement<number>[];
  threatsArea5yPct?: PlotReportFactElement<number>[];
  className?: string;
};

export const HabitatIntactnessThreatsHistoricTable = ({
  threatsTimeSeries,
  threatsAreaPct,
  threatsArea5yPct,
  className,
}: HabitatIntactnessThreatsHistoricTableProps) => {
  const { t } = useTranslation();

  const threatsData = Object.fromEntries(
    THREAT_TYPES.map((type) => {
      const threatData = threatsTimeSeries.filter((series) =>
        series.labels?.some((l) => l.name === PlotReportFactLabelNameEnum.threat_type && l.value === type),
      );

      return [type, threatData] as const;
    }).filter(([, threatData]) => threatData.length > 0),
  );

  const threatsDataMap = Object.fromEntries(
    Object.entries(threatsData).map(([type, facts]) => [type, getChartDataFromGraphFact(facts, factNames)]),
  );

  const sortedThreatsData = Object.fromEntries(
    Object.entries(threatsDataMap).map(([type, threatData]) => [
      type,
      sortChartData(threatData, (date) => printYear(new Date(date))),
    ]),
  );

  const dates = Array.from(new Set(Object.values(threatsDataMap).flatMap((threatData) => Object.keys(threatData))))
    .map((date) => parseInt(printYear(new Date(date)), 10))
    .sort((dateA, dateB) => new Date(dateA).getTime() - new Date(dateB).getTime());

  const firstTick = dates.at(0);
  const lastTick = dates.at(-1);

  const isAreaPctDataMissing = threatsAreaPct === undefined || threatsAreaPct.length === 0;
  const isArea5yPctDataMissing = threatsArea5yPct === undefined || threatsArea5yPct.length === 0;
  const isTimeSeriesDataMissing = !firstTick || !lastTick;

  const tableColumns = useMemo<TableColumn[]>(
    () => [
      {
        accessorKey: TABLE_KEYS.THREATS,
        header: ({ column }) => (
          <SortButton column={column}>
            {t('shared.ncaDetail.details.habitatIntactness.threatsTable.table.columns.threat')}
          </SortButton>
        ),
        cell: ({ getValue }) => <TableCell value={t(`global.threatTypes.${getValue<ThreatType>()}`)} />,
      },
      ...dates.map(
        (date) =>
          ({
            accessorKey: date.toString(),
            header: ({ column }) => <SortButton column={column}>{date.toString()}</SortButton>,
            cell: ({ getValue }) => <TableCell value={getValue<number | null>()} />,
          }) as TableColumn,
      ),
      ...(isAreaPctDataMissing
        ? []
        : [
            {
              accessorKey: TABLE_KEYS.AREA_PCT,
              header: ({ column }) => (
                <SortButton column={column}>
                  {t('shared.ncaDetail.details.habitatIntactness.threatsTable.table.columns.areaPct')}
                </SortButton>
              ),
              cell: ({ getValue }) => <TableCell value={getValue<number | null>()} />,
            } as TableColumn,
          ]),
      ...(isArea5yPctDataMissing
        ? []
        : [
            {
              accessorKey: TABLE_KEYS.AREA_5Y_PCT,
              header: ({ column }) => (
                <SortButton column={column}>
                  {t('shared.ncaDetail.details.habitatIntactness.threatsTable.table.columns.areaFiveYearPct')}
                </SortButton>
              ),
              cell: ({ getValue }) => <TableCell value={getValue<number | null>()} />,
            } as TableColumn,
          ]),
    ],
    [t, dates, isAreaPctDataMissing, isArea5yPctDataMissing],
  );

  const formattedData = useMemo(() => {
    return THREAT_TYPES.flatMap((threat) => {
      const threatData = sortedThreatsData[`${threat}`];

      const yearlyData =
        threatData && threatData.length > 0
          ? Object.fromEntries(
              threatData.map((yearData) => [
                [yearData.label as string],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                getAreaDisplay((yearData as any)?.[factNames.threat_areas] ?? null, t),
              ]),
            )
          : null;

      const threatsAreaValue = getAreaPctDisplay(
        threatsAreaPct?.find(
          (fact) =>
            fact?.labels?.some(
              (l) => l.name === PlotReportFactLabelNameEnum.threat_type && l.value === (threat as ThreatType),
            ) ?? false,
        ) ?? null,
        t,
      );

      const threatsArea5yPctValue = getAreaPctDisplay(
        threatsArea5yPct?.find(
          (fact) =>
            fact?.labels?.some(
              (l) => l.name === PlotReportFactLabelNameEnum.threat_type && l.value === (threat as ThreatType),
            ) ?? false,
        ) ?? null,
        t,
        true,
      );

      if (isNullish(yearlyData) && isNullish(threatsAreaValue) && isNullish(threatsArea5yPctValue)) {
        return [];
      }

      return [
        {
          [TABLE_KEYS.THREATS]: threat,
          ...yearlyData,
          [TABLE_KEYS.AREA_PCT]: threatsAreaValue,
          [TABLE_KEYS.AREA_5Y_PCT]: threatsArea5yPctValue,
        },
      ];
    });
  }, [sortedThreatsData, threatsAreaPct, threatsArea5yPct, t]);

  if (isAreaPctDataMissing && isTimeSeriesDataMissing && isArea5yPctDataMissing) {
    Logger.error('Not enough data to render the habitat intactness degradation drivers historical table');
    return (
      <div className={cn('h-full justify-center', className)}>
        <NoChartData />
      </div>
    );
  }

  return (
    <GenericTable
      data={formattedData}
      defaultColumns={tableColumns}
      columnOrder={[TABLE_KEYS.THREATS, ...dates.map((d) => d.toString()), TABLE_KEYS.AREA_PCT, TABLE_KEYS.AREA_5Y_PCT]}
      className={cn('border-none bg-[#fbfbfb]', className)}
      dataTestId='habitat-intactness-threats-historic-table'
    />
  );
};

const getAreaDisplay = (fact: GraphFact | null, t: TFunction): string | null => {
  const areaValue = formatGraphFactToArea(fact)?.toHectares()?.value ?? null;
  if (isNullish(areaValue)) {
    return null;
  }

  return `${getDisplayNumber(areaValue)} ${t('global.ui.unit.ha')}`;
};

const getAreaPctDisplay = (fact: PlotReportFactElement | null, t: TFunction, signed?: boolean): string | null => {
  if (isNullish(fact) || typeof fact?.value !== 'number') {
    return null;
  }

  if (fact.unit !== UnitEnum['%']) {
    Logger.error(`Expected percentage unit for ${fact.name} fact, but got ${fact.unit}`);
    return null;
  }

  return `${getDisplayNumber(fact.value, undefined, signed ? { signDisplay: 'exceptZero' } : undefined)} ${t('global.ui.unit.%')}`;
};

const TableCell = ({ value }: { value: string | number | null | undefined }) => {
  return <span className='typography-body1 text-secondary'>{isNullish(value) ? EMPTY_VALUE : value}</span>;
};
